import type { GraphQLConnectionQueryDefinition } from "./graphql-connection-query"
import type { GraphQLQueryDefinition, GraphQLQueryCodec } from "./graphql-query"
import type { RemoteCommandDefinition, RemoteCommandEventDefinitions } from "./remote-command"

type MaybePromise<T> = T | Promise<T>

export interface RemoteFakeContext {
  readonly signal?: AbortSignal
}

export interface GraphQLFakeContext extends RemoteFakeContext {
  readonly endpoint: string
  readonly document: string
}

export interface CommandFakeContext extends RemoteFakeContext {
  readonly route: string
}

export type QueryFakeHandler<TData, TVariables extends object> = (
  variables: Readonly<TVariables> | undefined,
  context: GraphQLFakeContext
) => MaybePromise<TData>

export type ConnectionQueryFakeHandler<TData, TVariables extends object> = (
  variables: Readonly<TVariables>,
  context: GraphQLFakeContext
) => MaybePromise<TData>

export type CommandFakeHandler<TInput, TResult> = (
  input: Readonly<TInput>,
  context: CommandFakeContext
) => MaybePromise<TResult>

interface QueryRemoteFakeDefinition {
  readonly kind: "query"
  readonly endpoint: string
  readonly document?: string
  readonly handler: (
    variables: Record<string, unknown> | undefined,
    context: GraphQLFakeContext
  ) => MaybePromise<unknown>
}

interface CommandRemoteFakeDefinition {
  readonly kind: "command"
  readonly route: string
  readonly handler: (input: unknown, context: CommandFakeContext) => MaybePromise<unknown>
}

export type RemoteFakeDefinition = QueryRemoteFakeDefinition | CommandRemoteFakeDefinition

export interface RemoteFakeRegistrar {
  register(...definitions: readonly RemoteFakeDefinition[]): () => void
  registerRoot(...definitions: readonly RemoteFakeDefinition[]): () => void
}

export function fakeQuery<TData, TParameters, TVariables extends object>(
  definition: GraphQLQueryDefinition<TData, TParameters, TVariables>,
  handler: QueryFakeHandler<TData, TVariables>
): RemoteFakeDefinition
export function fakeQuery<
  TData,
  TItem,
  TParameters,
  TVariables extends object,
  TSortField extends string,
  TBackendField extends string,
>(
  definition: GraphQLConnectionQueryDefinition<TData, TItem, TParameters, TVariables, TSortField, TBackendField>,
  handler: ConnectionQueryFakeHandler<TData, TVariables>
): RemoteFakeDefinition
export function fakeQuery(
  definition: { readonly endpoint: string; readonly document: string; readonly codec?: GraphQLQueryCodec },
  handler:
    QueryFakeHandler<unknown, Record<string, unknown>> | ConnectionQueryFakeHandler<unknown, Record<string, unknown>>
): RemoteFakeDefinition {
  return Object.freeze({
    kind: "query",
    endpoint: definition.endpoint,
    document: definition.document,
    handler:
      definition.codec === undefined
        ? (handler as QueryRemoteFakeDefinition["handler"])
        : async (variables, context) => {
            const values = variables === undefined ? undefined : definition.codec!.decodeVariables(variables)
            const data = await handler(values as Record<string, unknown>, context)
            return definition.codec!.result.encode(data)
          },
  })
}

/** Fakes all GraphQL documents sent to one endpoint. Intended for composed endpoints such as Root. */
export function fakeGraphQLEndpoint<TData, TVariables extends object = Record<string, unknown>>(
  endpoint: string,
  handler: QueryFakeHandler<TData, TVariables>
): RemoteFakeDefinition {
  return Object.freeze({
    kind: "query",
    endpoint,
    handler: handler as QueryRemoteFakeDefinition["handler"],
  })
}

export function fakeCommand<TInput, TResult, TEvents extends RemoteCommandEventDefinitions | undefined>(
  command: RemoteCommandDefinition<TInput, TResult, TEvents>,
  handler: CommandFakeHandler<TInput, TResult>
): RemoteFakeDefinition
export function fakeCommand<TInput, TResult>(
  command: string,
  handler: CommandFakeHandler<TInput, TResult>
): RemoteFakeDefinition
export function fakeCommand<TInput, TResult>(
  command: string | { readonly route: string },
  handler: CommandFakeHandler<TInput, TResult>
): RemoteFakeDefinition {
  const route = typeof command === "string" ? command : command.route
  return Object.freeze({
    kind: "command",
    route,
    handler: handler as CommandRemoteFakeDefinition["handler"],
  })
}

/** @internal */
export type RemoteFakeResolution<T> = { readonly handled: true; readonly value: T } | { readonly handled: false }

/** @internal */
export interface RemoteFakeResolver {
  resolveQuery<TData, TVariables extends object>(
    scopeIds: readonly string[],
    endpoint: string,
    document: string,
    variables: TVariables | undefined,
    signal?: AbortSignal
  ): MaybePromise<RemoteFakeResolution<TData>>

  resolveCommand<TResult, TInput>(
    scopeIds: readonly string[],
    route: string,
    input: TInput,
    signal?: AbortSignal
  ): MaybePromise<RemoteFakeResolution<TResult>>
}

/** @internal */
export interface RemoteFakeRegistry extends RemoteFakeResolver {
  createRegistrar(scopeId: string | null): RemoteFakeRegistrar
}

/** @internal */
export function createRemoteFakeRegistry(): RemoteFakeRegistry {
  interface RemoteFakeRegistration {
    readonly definition: RemoteFakeDefinition
  }

  const registrations = new Map<string | null, RemoteFakeRegistration[]>()

  const getScopeRegistrations = (scopeId: string | null) => {
    let scopeRegistrations = registrations.get(scopeId)
    if (scopeRegistrations == null) {
      scopeRegistrations = []
      registrations.set(scopeId, scopeRegistrations)
    }
    return scopeRegistrations
  }

  const findMatchingFake = (
    scopeIds: readonly string[],
    predicate: (definition: RemoteFakeDefinition) => boolean
  ): RemoteFakeDefinition | undefined => {
    for (const scopeId of [...scopeIds, null]) {
      const scopeRegistrations = registrations.get(scopeId)
      if (scopeRegistrations == null) continue

      for (let index = scopeRegistrations.length - 1; index >= 0; index -= 1) {
        const definition = scopeRegistrations[index]!.definition
        if (predicate(definition)) return definition
      }
    }

    return undefined
  }

  return {
    createRegistrar(scopeId) {
      const registerAt = (targetScopeId: string | null, definitions: readonly RemoteFakeDefinition[]) => {
        const scopeRegistrations = getScopeRegistrations(targetScopeId)
        const added = definitions.map((definition) => ({ definition }))
        scopeRegistrations.push(...added)
        let registered = true

        return () => {
          if (!registered) return
          registered = false

          for (const registration of added) {
            const index = scopeRegistrations.indexOf(registration)
            if (index >= 0) scopeRegistrations.splice(index, 1)
          }
          if (scopeRegistrations.length === 0) registrations.delete(targetScopeId)
        }
      }

      return Object.freeze({
        register(...definitions: readonly RemoteFakeDefinition[]) {
          return registerAt(scopeId, definitions)
        },
        registerRoot(...definitions: readonly RemoteFakeDefinition[]) {
          return registerAt(null, definitions)
        },
      })
    },

    resolveQuery<TData, TVariables extends object>(
      scopeIds: readonly string[],
      endpoint: string,
      document: string,
      variables: TVariables | undefined,
      signal?: AbortSignal
    ) {
      throwIfAborted(signal)
      const definition = findMatchingFake(
        scopeIds,
        (definition) =>
          definition.kind === "query" &&
          definition.endpoint === endpoint &&
          (definition.document == null || definition.document === document)
      )
      if (definition == null || definition.kind !== "query") {
        return { handled: false } as const
      }

      const result = definition.handler(
        variables as Record<string, unknown> | undefined,
        Object.freeze({
          endpoint,
          document,
          signal,
        })
      )
      return resolveFakeResult(result as MaybePromise<TData>, signal)
    },

    resolveCommand<TResult, TInput>(scopeIds: readonly string[], route: string, input: TInput, signal?: AbortSignal) {
      throwIfAborted(signal)
      const definition = findMatchingFake(
        scopeIds,
        (definition) => definition.kind === "command" && definition.route === route
      )
      if (definition == null || definition.kind !== "command") {
        return { handled: false } as const
      }

      const result = definition.handler(
        input,
        Object.freeze({
          route,
          signal,
        })
      )
      return resolveFakeResult(result as MaybePromise<TResult>, signal)
    },
  }
}

function resolveFakeResult<T>(result: MaybePromise<T>, signal?: AbortSignal): MaybePromise<RemoteFakeResolution<T>> {
  if (result instanceof Promise) {
    return result.then((value) => {
      throwIfAborted(signal)
      return { handled: true, value } as const
    })
  }

  throwIfAborted(signal)
  return { handled: true, value: result } as const
}

function throwIfAborted(signal?: AbortSignal) {
  if (!signal?.aborted) return
  if (signal.reason !== undefined) throw signal.reason
  throw new DOMException("The operation was aborted.", "AbortError")
}
